import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface DashboardStats {
  withoutCommit: number;
  overdue: number;
  withBinding: number;
  needsAttention: number;
  stale_7: number;
  stale_14: number;
}

export interface WorkspaceSeatInfo {
  usedSeats: number;
  maxSeats: number;
}

export interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  name: string | null;
}

export interface ActiveSession {
  id: string;
  summary: string | null;
  decision_type: string | null;
  decision_subtype: string | null;
  phase: number;
  subphase: string;
  updated_at: string;
  created_at: string;
  meta: any;
  my_role: string;
  participant_count: number;
  owner_name: string | null;
}

export interface TeamActivity {
  id: string;
  summary: string | null;
  decision_type: string | null;
  creator_name: string | null;
  updated_at: string;
  created_at: string;
}

async function fetchKpis(userId: string, workspaceId: string): Promise<DashboardStats> {
  const { data, error } = await supabase.rpc('get_dashboard_kpis', {
    _user_id: userId,
    _workspace_id: workspaceId,
  });
  if (error) throw error;
  const result = data as any;
  return {
    withBinding: result?.withBinding ?? 0,
    withoutCommit: result?.withoutCommit ?? 0,
    overdue: result?.overdue ?? 0,
    needsAttention: result?.needsAttention ?? 0,
    stale_7: result?.stale_7 ?? 0,
    stale_14: result?.stale_14 ?? 0,
  };
}

async function fetchSessionsData(userId: string, workspaceId: string) {
  // Group 1: user participations + profile in parallel
  const [participationsRes, profileRes] = await Promise.all([
    supabase
      .from('decision_participants')
      .select('session_id, role')
      .eq('user_id', userId),
    supabase
      .from('profiles')
      .select('first_name, last_name, name')
      .eq('id', userId)
      .single(),
  ]);

  if (participationsRes.error) throw participationsRes.error;

  const participations = participationsRes.data || [];
  const userProfile: UserProfile | null = profileRes.data;
  const sessionIds = participations.map(p => p.session_id);

  if (sessionIds.length === 0) {
    return {
      sessions: [] as ActiveSession[],
      userProfile,
    };
  }

  // Group 2: sessions + all participants for those sessions in parallel
  const [sessionsRes, allParticipantsRes] = await Promise.all([
    supabase
      .from('decision_sessions')
      .select('id, summary, decision_type, decision_subtype, phase, subphase, updated_at, created_at, meta')
      .in('id', sessionIds)
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false }),
    supabase
      .from('decision_participants')
      .select('session_id, role, profiles!decision_participants_user_id_fkey(name)')
      .in('session_id', sessionIds),
  ]);

  if (sessionsRes.error) throw sessionsRes.error;

  const roleMap = new Map(participations.map(p => [p.session_id, p.role]));
  const countMap = new Map<string, number>();
  const ownerMap = new Map<string, string | null>();

  for (const p of allParticipantsRes.data || []) {
    countMap.set(p.session_id, (countMap.get(p.session_id) || 0) + 1);
    if (p.role === 'owner') {
      ownerMap.set(p.session_id, (p.profiles as any)?.name || null);
    }
  }

  const sessions: ActiveSession[] = (sessionsRes.data || []).map(s => ({
    ...s,
    my_role: roleMap.get(s.id) || 'viewer',
    participant_count: countMap.get(s.id) || 1,
    owner_name: ownerMap.get(s.id) || null,
  }));

  return {
    sessions,
    userProfile,
  };
}

async function fetchMetaData(userId: string, workspaceId: string) {
  const [activityRes, workspaceRes, membersRes, invitesRes] = await Promise.all([
    supabase
      .from('activity_logs')
      .select(`
        id, activity_type, entity_type, entity_id, metadata, created_at,
        profiles!activity_logs_user_id_fkey(name)
      `)
      .eq('workspace_id', workspaceId)
      .neq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(10),
    supabase
      .from('workspaces')
      .select('max_seats')
      .eq('id', workspaceId)
      .single(),
    supabase
      .from('workspace_members')
      .select('id', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .eq('seat_status', 'active'),
    supabase
      .from('workspace_invites')
      .select('id', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .is('accepted_at', null)
      .gt('expires_at', new Date().toISOString()),
  ]);

  if (activityRes.error) throw activityRes.error;

  const teamActivities: TeamActivity[] = (activityRes.data || []).map((item: any) => ({
    id: item.id,
    summary: item.metadata?.summary || item.metadata?.decision_type || item.activity_type,
    decision_type: item.metadata?.decision_type || null,
    creator_name: item.profiles?.name || null,
    updated_at: item.created_at,
    created_at: item.created_at,
  }));

  const seatInfo: WorkspaceSeatInfo | null = workspaceRes.data
    ? {
        usedSeats: (membersRes.count || 0) + (invitesRes.count || 0),
        maxSeats: workspaceRes.data.max_seats,
      }
    : null;

  return { teamActivities, seatInfo };
}

export function useDashboardData(workspaceId: string | null) {
  const { user } = useAuth();
  const userId = user?.id;

  const sessionsQuery = useQuery({
    queryKey: ['dashboard-sessions', userId, workspaceId],
    queryFn: () => fetchSessionsData(userId!, workspaceId!),
    enabled: !!userId && !!workspaceId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const kpiQuery = useQuery({
    queryKey: ['dashboard-kpis', userId, workspaceId],
    queryFn: () => fetchKpis(userId!, workspaceId!),
    enabled: !!userId && !!workspaceId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const metaQuery = useQuery({
    queryKey: ['dashboard-meta', userId, workspaceId],
    queryFn: () => fetchMetaData(userId!, workspaceId!),
    enabled: !!userId && !!workspaceId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  return {
    stats: kpiQuery.data ?? { withoutCommit: 0, overdue: 0, withBinding: 0, needsAttention: 0, stale_7: 0, stale_14: 0 },
    activeSessions: sessionsQuery.data?.sessions ?? [],
    userProfile: sessionsQuery.data?.userProfile ?? null,
    teamActivities: metaQuery.data?.teamActivities ?? [],
    seatInfo: metaQuery.data?.seatInfo ?? null,
    loading: sessionsQuery.isLoading || metaQuery.isLoading || kpiQuery.isLoading,
    error: sessionsQuery.error || metaQuery.error || kpiQuery.error,
    refetch: () => {
      sessionsQuery.refetch();
      kpiQuery.refetch();
      metaQuery.refetch();
    },
  };
}
